/**
 * @file interviewRespond.js
 * @description Client side of the one-tap interview response flow.
 *
 *   A therapist gets an interview invite email with one button per proposed
 *   time. Those buttons point at `/interview-respond?<signed params>` on
 *   abacases.com — NOT at the edge function, for two reasons: the edge-function
 *   gateway rewrites Content-Type to text/plain so a function cannot serve a
 *   page, and a `<ref>.supabase.co` link in an inbox reads as phishing.
 *
 *   What lives here is transport only. The page this feeds is presentation; it
 *   never decides whether an action is allowed. The signed params are passed
 *   through untouched to `interview-respond`, which re-verifies the HMAC on
 *   every request — GET for a preview, POST to actually perform the action.
 *
 *   Deliberately does NOT use the shared supabase client: this page must work
 *   logged out, must not attach the reader's session to the call (mail gets
 *   forwarded, and `info@` addresses are shared inboxes), and the function runs
 *   with `verify_jwt = false` so no key is needed.
 *
 * @module lib/interviewRespond
 */
import { supabaseUrl } from "./supabase";

/** Public route the email links point at. Intentionally NOT in seoRoutes.json. */
export const INTERVIEW_RESPOND_PATH = "/interview-respond";

const FN_URL = `${supabaseUrl}/functions/v1/interview-respond`;

/** True when the given pathname is the interview-respond route (trailing slash tolerated). */
export function isInterviewRespondPath(pathname) {
  return String(pathname || "").replace(/\/+$/, "") === INTERVIEW_RESPOND_PATH;
}

/**
 * Pull the signed action params out of a query string.
 * `s` (the slot index / outcome value) is legitimately empty for `decline` and
 * `none`, so it is not part of the presence check — but it IS signed, so it is
 * always forwarded verbatim. `f` (the fingerprint of the slot a confirm link
 * books) is signed too and forwarded when present; older links have none.
 * @param {string} search e.g. window.location.search
 * @returns {URLSearchParams|null} null when the link is obviously incomplete
 */
export function readActionParams(search) {
  let q;
  try { q = new URLSearchParams(search || ""); } catch { return null; }
  const i = q.get("i"), a = q.get("a"), e = q.get("e"), sig = q.get("sig");
  if (!i || !a || !e || !sig) return null;
  const out = new URLSearchParams();
  out.set("i", i);
  out.set("a", a);
  out.set("s", q.get("s") || "");
  out.set("e", e);
  const f = q.get("f");
  if (f) out.set("f", f);
  out.set("sig", sig);
  return out;
}

/**
 * Call the edge function with the signed params.
 *
 * GET is a preview and never writes. POST performs the action. Params travel in
 * the query string and no request body or Content-Type is sent, which keeps the
 * POST a CORS "simple request" (no preflight) while the function still enforces
 * its own Origin allow-list.
 *
 * @param {"GET"|"POST"} method
 * @param {URLSearchParams} params signed params from readActionParams
 * @returns {Promise<{ok: boolean, state: string, [k: string]: any}>}
 *   Always resolves. Transport failures surface as `{ state: "network" }` so
 *   the page can tell "we couldn't reach the server" apart from "the server
 *   said no".
 */
export async function callInterviewRespond(method, params) {
  let res;
  try {
    res = await fetch(`${FN_URL}?${params.toString()}`, {
      method,
      // No cookies, no Authorization header: acting on this link must never
      // ride on, or create, a session.
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
  } catch {
    return { ok: false, state: "network", reason: "fetch_failed" };
  }
  // The gateway serves this as text/plain regardless of what the function sets,
  // so parse the text rather than trusting res.json()'s content-type path.
  let body = null;
  try { body = JSON.parse(await res.text()); } catch { /* fall through */ }
  if (!body || typeof body !== "object") {
    return { ok: false, state: "network", reason: "bad_response", httpStatus: res.status };
  }
  return { ...body, httpStatus: res.status };
}
